/**
 * React Query hooks for Users data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, getUserById, updateUser } from '@/lib/api/users';
import type { UpdateUserRequest } from '@/types/api';
import { userKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { userKeys };

/**
 * Fetch all users (organizers only)
 */
export function useUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: getUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch a single user by ID
 */
export function useUser(id: number) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => getUserById(id),
    staleTime: 60 * 60 * 1000, // 1 hour - user names rarely change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });
}

/**
 * Update user mutation
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      updateUser(id, data),
    onSuccess: () => {
      // Invalidate users list and individual user queries
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
